import { SessionService } from '@src/app/shared/services/session.service';
import { WarrantyExtensionRequest } from '@src/app/shared/backend/clients/model/warrantyExtensionRequest';
import { WarrantyVoidRequest } from '@src/app/shared/backend/clients/model/warrantyVoidRequest';
import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Table } from 'primeng/table';
import { StatusPipe } from '@src/app/shared/pipes/status.pipe';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { WarrantiesService, RetailersService, RetailerResponse, WarrantyDisassociateRequest, WarrantyDetailsResponse, WarrantyOperationDetailsResponse, WarrantyOperationType, FilterRequestForCombo, WarrantyVoidLastExtensionRequest, WarrantyGlobalDetailsResponse, WarrantyOperationGlobalDetailsResponse } from '@src/app/shared/backend/clients';
import { FriendlyBooleanPipe } from '@src/app/shared/pipes/friendlyBoolean.pipe';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { DialogReasonComponent } from '@src/app/shared/components/dialog-reason/dialog-reason.component';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

interface WarrantyNavigationState {
  warranty?: WarrantyGlobalDetailsResponse;
}

interface WarrantyViewData {
  id?: number;
  code: string;
  activationDate?: string | null;
  endDate?: string | null;
  isPrepaired: boolean;
  isActive: boolean;
  timesUsed: number;
  useLimitReached: boolean;
  hasD2C: boolean;
  isItemStolen?: boolean;
  itemId?: number | null;
  saleRetailerId?: number | null;
  itemBrandId?: number | null;
}

interface WarrantyOperationViewData {
  id?: number;
  operationDate: string;
  itemId?: number | null;
  itemSerialNumber?: string | null;
  productSku?: string | null;
  durationInMonths?: number | null;
  warrantyOperationType: WarrantyOperationType;
  comment?: string | null;
  retailerId?: number | null;
  createdById?: string;
  createdByFullName?: string;
  createdOn?: string;
  revertedById?: number | null;
}

@Component({
    selector: 'app-view-warranty',
    templateUrl: './view-warranty.component.html',
    styleUrl: './view-warranty.component.scss',
    host: {
        class: 'app-view-warranty'
    },
    providers: [StatusPipe, ConfirmationService, FriendlyBooleanPipe],
    standalone: false
})
export class ViewWarrantyComponent extends BaseViewComponent implements OnInit {

  @ViewChild('dialog') dialog: DialogReasonComponent | undefined;

  isGlobalSearch: boolean = false;
  globalWarrantyData: WarrantyGlobalDetailsResponse | null = null;

  delete: boolean = false;
  allowDisassociateWarranty: boolean = false;
  allowVoidWarranty: boolean = false;
  allowExtendWarranty: boolean = false;
  allowViewItems: boolean = false;
  warrantyId: number = 0;
  allowItemOperationsSoldByAnotherRetailer: boolean = false;

  warrantyData: WarrantyViewData = {
    code: '',
    isPrepaired: false,
    isActive: false,
    timesUsed: 0,
    useLimitReached: false,
    hasD2C: false,
    isItemStolen: false
  };

  warrantyOperationData: WarrantyOperationViewData[] = [];
  filteredWarrantyOperationData: WarrantyOperationViewData[] = [];

  canBeDeleted: boolean = false;
  canExtendD2C: boolean = false;
  extendEnabled: boolean = false;

  displayVoidWarrantyModalbox: boolean = false;
  displayDisassociateWarrantyModalbox: boolean = false;
  displayVoidWarrantyExtensionModalbox: boolean = false;
  displayExtendWarrantyModalbox: boolean = false;

  latestOperation: any = null;
  filterOperator = FilterOperator;
  warrantyOperationType = WarrantyOperationType;

  store: RetailerResponse = {};
  storesList: any[] = [];
  storesTotalRecords: number = 0;
  storesRowsPerPageOption: number = 10;

  companyIsPrepaired: boolean = false;

  @ViewChild('dtWarrantyHistoryTable') table: Table | undefined;

  currentDate: Date = new Date();

  public voidWarrantyFormGroup: FormGroup = this.fb.group({
    warrantyCode: [''],
    manualVoidDate: [true],
    voidDate: [this.currentDate],
    retailer: [[], [Validators.required]],
    voidReason: ['', [Validators.required, Validators.maxLength(128)]],
  });

  public voidWarrantyExtensionFormGroup: FormGroup = this.fb.group({
    warrantyCode: [''],
    voidDate: [this.currentDate],
    retailer: [[], [Validators.required]],
  });

  public disassociateWarrantyFormGroup: FormGroup = this.fb.group({
    warrantyCode: [''],
    manualDisassociateDate: [true],
    disassociateDate: [this.currentDate],
    retailer: [[], [Validators.required]],
    disassociateReason: ['', [Validators.required, Validators.maxLength(128)]],
  });

  public extendWarrantyFormGroup: FormGroup = this.fb.group({
    warrantyCode: [''],
    durationInMonths: ['', [Validators.required]],
    isD2C: ['false'],
    retailer: [[], [Validators.required]],
    extendReason: ['', [Validators.required, Validators.maxLength(128)]],
  });

  constructor(
    private navigationLocation: Location,
    private sessionService: SessionService,
    private permissionService: PermissionService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private retailersService: RetailersService,
    private warrantiesService: WarrantiesService,
    private confirmationService: ConfirmationService,
    private changeDetector: ChangeDetectorRef) { super(); }

  async ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    const company = await this.sessionService.getCompanyData();

    this.allowItemOperationsSoldByAnotherRetailer =
      company.allowItemOperationsSoldByAnotherRetailer ?? false;

    this.companyIsPrepaired = company.isPrepaired ?? false;
    this.isGlobalSearch = this.route.snapshot.data['isGlobalSearch'] === true;

    if (this.isGlobalSearch) {
      // router.navigate(..., { state }) stores data in browser history; Location.getState() reads it back!
      // That keeps warranty details available after refresh without doin API calls
      const state = this.navigationLocation.getState() as WarrantyNavigationState;

      if (!state.warranty) {
        await this.router.navigate(['/warranties'], {
          queryParams: {
            tab: 'global-search'
          },
          replaceUrl: true
        });
        return;
      }

      const warranty = state.warranty;
      this.globalWarrantyData = warranty;
      this.warrantyData = warranty;

      this.warrantyOperationData = [...(warranty.warrantyOperations ?? [])];

      // Global operations have no ID, so sort by date
      this.warrantyOperationData.sort((a, b) => {
        const firstDate = new Date(a.operationDate).getTime();
        const secondDate = new Date(b.operationDate).getTime();

        return secondDate - firstDate;
      });

      if (company.implicitSaleCreation) {
        this.filteredWarrantyOperationData = this.warrantyOperationData.filter(
          operation =>
            operation.warrantyOperationType !== WarrantyOperationType.Associate &&
            operation.warrantyOperationType !== WarrantyOperationType.Disassociate
        );
      } else {
        this.filteredWarrantyOperationData = [...this.warrantyOperationData];
      }

      this.latestOperation = this.warrantyOperationData[0] ?? null;

      this.changeDetector.detectChanges();

      // Global details are read-only so skip API call & permission checks!
      return;
    }

    this.route.params.subscribe(async params => {
      this.warrantyId = params['warrantyId'];
      await this.get();
    });

    const isD2CControl = this.extendWarrantyFormGroup.get('isD2C');
    const durationControl = this.extendWarrantyFormGroup.get('durationInMonths');

    isD2CControl?.valueChanges.subscribe(value => {
      const isTrue = value === true || value === 'true';

      if (isTrue) {
        durationControl?.reset();
        durationControl?.clearValidators();
      } else {
        durationControl?.setValidators([Validators.required]);
      }

      durationControl?.updateValueAndValidity();
    });

    this.extendWarrantyFormGroup.get('retailer')?.valueChanges.subscribe(async value => {
      const retailerId = value?.[0];

      if (retailerId != null) {
        await this.onRetailerChanged(retailerId);
      } else {
        this.canExtendD2C = false;
      }
    });

    this.delete = await this.permissionService.hasActionPermissions(Actions.delete);
    this.allowDisassociateWarranty = await this.permissionService.hasActionPermissions(Actions.activate_warranties);
    this.allowVoidWarranty = await this.permissionService.hasActionPermissions(Actions.void_warranties);
    this.allowExtendWarranty = await this.permissionService.hasActionPermissions(Actions.extend_warranties);
    this.allowViewItems = await this.permissionService.hasActionPermissions(Actions.view_items);
  }

  override async get() {
    if (this.isGlobalSearch) {
      return;
    }

    try {

      const response = await firstValueFrom(this.warrantiesService.apiWarrantiesDetailsIdGet(this.warrantyId));
      this.warrantyData = response;

      // sort operation to display recent at top
      this.warrantyOperationData = [...(response.warrantyOperations ?? [])]
        .sort((a, b) => b.id - a.id);

      if (this.warrantyData.endDate && new Date(this.warrantyData.endDate) < new Date() && this.warrantyData.isActive) {
        const lastActivation = this.warrantyOperationData
          .filter(x => x.warrantyOperationType === WarrantyOperationType.Activate && x.operationDate)
          .sort((a, b) => new Date(b.operationDate).getTime() - new Date(a.operationDate).getTime())[0];

        this.extendEnabled = (await firstValueFrom(this.warrantiesService.apiWarrantiesCanExtendExpiredRetailerIdGet(lastActivation.retailerId ?? 0))).response ?? false;
      }
      else {
        this.extendEnabled = this.warrantyData.isActive ?? false;
      }

      const companyData = await this.sessionService.getCompanyData();
      this.companyIsPrepaired = companyData.isPrepaired ?? false;

      // UX: hide Associate/Disassociate operation for implicit sale companies
      this.filteredWarrantyOperationData = companyData.implicitSaleCreation
        ? this.warrantyOperationData.filter(
          operation =>
            operation.warrantyOperationType !== WarrantyOperationType.Associate &&
            operation.warrantyOperationType !== WarrantyOperationType.Disassociate
        )
        : [...this.warrantyOperationData];

      this.canBeDeleted = true;

      if (this.warrantyOperationData.length > 0) {
        let mostRecent = this.warrantyOperationData[0];

        for (const operation of this.warrantyOperationData) {
          const currentDate = new Date(operation.operationDate);
          const latestDate = new Date(mostRecent.operationDate);

          if (currentDate > latestDate) {
            mostRecent = operation;
          }
        }

        this.latestOperation = mostRecent;
      }
    } catch (error) {
      console.log('Error fetching warranty data:', error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  async onLazyLoadStores(event: any) {
    await this.getStores(event);
  }

  async getStores(event: any) {
    const filters: any[] = [];
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload: FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.storesRowsPerPageOption
    };

    if (search) {
      payload.textFilter = search;
    }

    filters.push({
      propertyName: 'isActive',
      operator: 'eq',
      values: ['true']
    });


    if(!this.allowItemOperationsSoldByAnotherRetailer && this.warrantyData.saleRetailerId != null)
    {
      filters.push({
            propertyName: 'id',
            operator: 'eq',
            values: [this.warrantyData.saleRetailerId.toString()]
          });
    }

    if(this.warrantyData.itemId != null)
    {
      filters.push({
            propertyName: 'brands.id',
            operator: 'in',
            values: [this.warrantyData.itemBrandId?.toString()]
          });
    }

    payload.additionalFilters = filters;

    const storesPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(payload));
    this.storesList = storesPage.results ?? [];
    this.storesTotalRecords = storesPage.totalCount ?? 0;

    if(this.storesTotalRecords == 1)
      {
          this.extendWarrantyFormGroup.get('retailer')?.setValue(
                this.storesList.map((r: any) => r.id),
                { emitEvent: false }
              );
          this.voidWarrantyFormGroup.get('retailer')?.setValue(
                this.storesList.map((r: any) => r.id),
                { emitEvent: false }
              );
          this.voidWarrantyExtensionFormGroup.get('retailer')?.setValue(
                this.storesList.map((r: any) => r.id),
                { emitEvent: false }
              );
      }   
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  onTriggerVoidDisassociate() {
    this.onHideDisassociateWarrantyAction();
    this.displayDisassociateWarrantyModalbox = true;
  }

  onTriggerVoidWarranty() {
    this.onHideVoidWarrantyAction();
    this.getStores(null);
    this.displayVoidWarrantyModalbox = true;
  }

  onTriggerVoidWarrantyExtension() {
    this.onHideVoidWarrantyExtensionAction();
    this.getStores(null);
    this.displayVoidWarrantyExtensionModalbox = true;
  }

  onTriggerExtendWarranty() {
    this.onHideExtendWarrantyAction();
    const retailerId = this.extendWarrantyFormGroup.controls['retailer'].value?.[0];
    if (retailerId != null) {
      this.onRetailerChanged(retailerId);
    }
    this.getStores(null);
    this.displayExtendWarrantyModalbox = true;
  }

  onHideExtendWarrantyAction() {
    this.extendWarrantyFormGroup.reset({
      warrantyCode: '',
      durationInMonths: '',
      isD2C: 'false',
      retailer: [],
      extendReason: ''
    });

    this.extendWarrantyFormGroup.markAsPristine();
    this.extendWarrantyFormGroup.markAsUntouched();
    this.canExtendD2C = false;
  }

  onHideVoidWarrantyAction() {
    this.currentDate = new Date();

    this.voidWarrantyFormGroup.reset({
      warrantyCode: '',
      manualVoidDate: true,
      voidDate: this.currentDate,
      retailer: [],
      voidReason: ''
    });

    this.voidWarrantyFormGroup.markAsPristine();
    this.voidWarrantyFormGroup.markAsUntouched();
  }

  onHideVoidWarrantyExtensionAction() {
    this.currentDate = new Date();

    this.voidWarrantyExtensionFormGroup.reset({
      warrantyCode: '',
      voidDate: this.currentDate,
      retailer: [],
    });

    this.voidWarrantyExtensionFormGroup.markAsPristine();
    this.voidWarrantyExtensionFormGroup.markAsUntouched();
  }

  onHideDisassociateWarrantyAction() {
    this.currentDate = new Date();

    this.disassociateWarrantyFormGroup.reset({
      warrantyCode: '',
      manualDisassociateDate: true,
      disassociateDate: this.currentDate,
      retailer: [],
      disassociateReason: ''
    });

    this.disassociateWarrantyFormGroup.markAsPristine();
    this.disassociateWarrantyFormGroup.markAsUntouched();
  }

  onCancelExtendWarranty() {
    this.displayExtendWarrantyModalbox = false;
    this.onHideExtendWarrantyAction();
  }

  async onConfirmExtendWarranty() {
    this.extendWarrantyFormGroup.controls['warrantyCode'].setValue(this.warrantyData.code);

    if (this.extendWarrantyFormGroup.valid) {

      const dateObj = new Date();
      const utcDate = dateObj.toISOString();

      const payload: WarrantyExtensionRequest = {
        code: this.extendWarrantyFormGroup.controls['warrantyCode'].value,
        comment: this.extendWarrantyFormGroup.controls['extendReason'].value,
        durationInMonths: this.extendWarrantyFormGroup.controls['durationInMonths'].value,
        operationDate: utcDate,
        retailerId: this.extendWarrantyFormGroup.controls['retailer'].value?.[0],
        isD2C: this.extendWarrantyFormGroup.controls['isD2C'].value  === 'true',
      };

      await firstValueFrom(this.warrantiesService.apiWarrantiesExtendPost(payload));

      this.displayExtendWarrantyModalbox = false;
      this.get();
      this.onHideExtendWarrantyAction();

      this.extendWarrantyFormGroup.reset({
        warrantyCode: '',
        durationInMonths: '',
        isD2C: 'false',
        retailer: null,
        extendReason: ''
      });

      this.canExtendD2C = false;
      }

    this.confirmationService.close();
  }

  onCancelVoidWarranty() {
    this.displayVoidWarrantyModalbox = false;
    this.onHideVoidWarrantyAction();
  }

  onCancelVoidWarrantyExtension() {
    this.displayVoidWarrantyExtensionModalbox = false;
    this.onHideVoidWarrantyExtensionAction();
  }

  onCancelDisassociateWarranty() {
    this.displayDisassociateWarrantyModalbox = false;
    this.onHideDisassociateWarrantyAction();
  }

  async onConfirmVoidWarranty() {

    this.voidWarrantyFormGroup.controls['warrantyCode'].setValue(this.warrantyData.code);

    if (this.voidWarrantyFormGroup.valid) {
      let voidDate;
      if (this.voidWarrantyFormGroup.controls['manualVoidDate'].value === true) {
        voidDate = this.voidWarrantyFormGroup.controls['voidDate'].value
      } else {
        voidDate = new Date();
      }
      const voidUtcDate = voidDate.toISOString();

      const payload: WarrantyVoidRequest = {
        code: this.voidWarrantyFormGroup.controls['warrantyCode'].value,
        comment: this.voidWarrantyFormGroup.controls['voidReason'].value,
        operationDate: voidUtcDate,
        retailerId: this.voidWarrantyFormGroup.controls['retailer'].value?.[0]
      };

      await firstValueFrom(this.warrantiesService.apiWarrantiesVoidPost(payload));

      if (!this.warrantyData.isPrepaired) {
        const payload: WarrantyDisassociateRequest = {
          code: this.voidWarrantyFormGroup.controls['warrantyCode'].value,
          comment: this.voidWarrantyFormGroup.controls['voidReason'].value,
          operationDate: voidUtcDate,
          retailerId: this.voidWarrantyFormGroup.controls['retailer'].value?.[0]
        };
        await firstValueFrom(this.warrantiesService.apiWarrantiesDisassociatePost(payload))
      }

      this.warrantyData.isActive = false;
      this.displayVoidWarrantyModalbox = false;

      this.get();
    }

    this.displayVoidWarrantyModalbox = false;
    this.onHideVoidWarrantyAction();
  }

  async onConfirmVoidWarrantyExtension() {
    this.voidWarrantyExtensionFormGroup.controls['warrantyCode'].setValue(this.warrantyData.code);
    if (this.voidWarrantyExtensionFormGroup.valid) {
      let voidDate;
      voidDate = new Date();
      const voidUtcDate = voidDate.toISOString();

      const payload: WarrantyVoidLastExtensionRequest = {
        code: this.voidWarrantyExtensionFormGroup.controls['warrantyCode'].value,
        operationDate: voidUtcDate,
        retailerId: this.voidWarrantyExtensionFormGroup.controls['retailer'].value?.[0]
      };

      await firstValueFrom(this.warrantiesService.apiWarrantiesVoidExtensionPost(payload));

      this.displayVoidWarrantyExtensionModalbox = false;

      this.get();
    }

    this.displayVoidWarrantyModalbox = false;
    this.onHideVoidWarrantyAction();
  }
  async onConfirmDisassociateWarranty() {

    this.disassociateWarrantyFormGroup.controls['warrantyCode'].setValue(this.warrantyData.code);

    if (this.disassociateWarrantyFormGroup.valid) {
      const useManualDate =this.disassociateWarrantyFormGroup.controls['manualDisassociateDate'].value;

      const disassociateDate = useManualDate
        ? new Date(this.disassociateWarrantyFormGroup.controls['disassociateDate'].value)
        : new Date();

      const payload: WarrantyDisassociateRequest = {
        code: this.disassociateWarrantyFormGroup.controls['warrantyCode'].value,
        comment: this.disassociateWarrantyFormGroup.controls['disassociateReason'].value,
        operationDate: disassociateDate.toISOString(),
        retailerId: this.disassociateWarrantyFormGroup.controls['retailer'].value?.[0]
      };

      await firstValueFrom(this.warrantiesService.apiWarrantiesDisassociatePost(payload));

      await this.get();
    }

    this.displayDisassociateWarrantyModalbox = false;
    this.onHideDisassociateWarrantyAction();
  }

  getLatestExtendOperation() {
    return [...(this.warrantyOperationData ?? [])]
    .find(
      operation =>
        operation.warrantyOperationType === WarrantyOperationType.Extend && operation.revertedById == null
    );
  }

  async onRetailerChanged(retailerId: number) {
    this.canExtendD2C =
      (await firstValueFrom(
        this.warrantiesService.apiWarrantiesCanExtendD2cWarrantyRetailerIdItemIdGet(retailerId, this.warrantyData.itemId ?? 0)
      )).response ?? false;
  }


  async showDeleteDialog(): Promise<void> {
    const deleteCheckResponses = await firstValueFrom(this.warrantiesService.apiWarrantiesDeleteCheckBatchPost([this.warrantyId]));

    this.dialog?.show(deleteCheckResponses);
  }

  async deleteWarranty(doDelete: boolean): Promise<void> {
    if (doDelete) {
      await firstValueFrom(this.warrantiesService.apiWarrantiesDeleteIdDelete(this.warrantyId));
      this.router.navigate([`/warranties`]);
    }
  }
}
